
//#region Imports
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, useMap, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';
import 'leaflet/dist/leaflet.css';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import axios from "axios";
import { Pagination, Button, Offcanvas } from 'react-bootstrap';
import { API_ENDPOINTS } from "../../configs/apiConfig";
import BrandSelect from "../common/BrandSelect";

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import subTypes from './subtypes.js';
import '../../styles/Geo.css';

import { FaSort, FaSortUp, FaSortDown, FaExpand, FaSlidersH, FaLightbulb, FaSearch } from "react-icons/fa";
//#endregion

//#region Configurações Leaflet
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const COLORS = ['#339288', '#2aa44e', '#04d0c2', '#00353d', '#3c8c71', '#144a50'];
//#endregion

//#region Hook useResizeDetector
const useResizeDetector = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    zoom: window.devicePixelRatio
  });
  const [forceUpdate, setForceUpdate] = useState(0);

  const debounceRef = useRef(null);

  const handleResize = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const newDimensions = {
        width: window.innerWidth,
        height: window.innerHeight,
        zoom: window.devicePixelRatio
      };

      const widthChanged = Math.abs(newDimensions.width - dimensions.width) > 10;
      const heightChanged = Math.abs(newDimensions.height - dimensions.height) > 10;
      const zoomChanged = Math.abs(newDimensions.zoom - dimensions.zoom) > 0.1;

      if (widthChanged || heightChanged || zoomChanged) {
        setDimensions(newDimensions);
        setTimeout(() => {
          setForceUpdate(prev => prev + 1);
        }, 100);
      }
    }, 150);
  }, [dimensions]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    const handleZoom = () => {
      setTimeout(handleResize, 50);
    };

    window.addEventListener('wheel', handleZoom, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('wheel', handleZoom);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [handleResize]);

  return { dimensions, forceUpdate };
};
//#endregion

//#region Componente ResponsiveChartContainer
const ResponsiveChartContainer = ({ children, forceUpdate }) => {
  const containerRef = useRef(null);
  const [containerKey, setContainerKey] = useState(0);

  useEffect(() => {
    if (forceUpdate > 0) {
      setContainerKey(prev => prev + 1);
    }
  }, [forceUpdate]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer key={containerKey} width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
};
//#endregion

//#region Componente HeatmapLayer
const HeatmapLayer = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (!points || points.length === 0) return;

    if (!map || !map.getContainer()) return;

    const container = map.getContainer();
    if (container.offsetWidth === 0 || container.offsetHeight === 0) {
      const checkDimensions = () => {
        if (container.offsetWidth > 0 && container.offsetHeight > 0) {
          addHeatLayer();
        } else {
          setTimeout(checkDimensions, 100);
        }
      };
      checkDimensions();
      return;
    }

    const addHeatLayer = () => {
      const maxIntensity = Math.max(...points.map(p => p[2])) || 1;

      const heatLayer = L.heatLayer(points, {
        radius: 30,
        blur: 20,
        maxZoom: 12,
        gradient: {
          0.2: 'blue',
          0.4: 'cyan',
          0.6: 'lime',
          0.8: 'yellow',
          1: 'red'
        },
        minOpacity: 0.3,
        max: maxIntensity
      }).addTo(map);

      return () => {
        if (map && heatLayer) {
          map.removeLayer(heatLayer);
        }
      };
    };

    const cleanup = addHeatLayer();
    return cleanup;
  }, [map, points]);

  return null;
};
//#endregion

//#region Componente para desenhar o círculo de raio no mapa
const RadiusCircle = ({ center, radius }) => {
  const map = useMap();

  useEffect(() => {
    if (!center) return;

    const circle = L.circle(center, {
      radius: radius * 1000, // Leaflet usa metros, então convertemos km para m
      color: '#339288',
      fillColor: '#339288',
      fillOpacity: 0.15,
      weight: 2,
    }).addTo(map);

    // Centraliza o mapa no círculo
    map.fitBounds(circle.getBounds());

    return () => {
      map.removeLayer(circle);
    };
  }, [map, center, radius]);

  return null;
};
//#endregion

//#region Componente para capturar cliques no mapa
const MapClickHandler = ({ onMapClick }) => {
  const map = useMap();

  useEffect(() => {
    const handleClick = (e) => {
      onMapClick(e.latlng);
    };

    map.on('click', handleClick);

    return () => {
      map.off('click', handleClick);
    };
  }, [map, onMapClick]);

  return null;
};
//#endregion

//#region Componente Principal Geo
const Geo = () => {
  const { forceUpdate } = useResizeDetector();

  const [accessData, setAccessData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMarkers, setShowMarkers] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  // Drawer de filtros avançados + aba de gráfico ativa + preset de período
  const [showDrawer, setShowDrawer] = useState(false);
  const [activeChart, setActiveChart] = useState('estados');
  const [periodPreset, setPeriodPreset] = useState('');

  // Estados para o filtro de raio
  const [radiusFilterCenter, setRadiusFilterCenter] = useState(null); // [lat, lng]
  const [radius, setRadius] = useState(100); // Raio em km, com valor padrão
  const [isRadiusFilterActive, setIsRadiusFilterActive] = useState(false);

  const [chapters, setChapters] = useState([]);
  const [tags, setTags] = useState([]);
  const [statesFromIBGE, setStatesFromIBGE] = useState([]);

  const [tempFilters, setTempFilters] = useState({
    selectedChapter: '',
    selectedTag: '',
    startDate: '',
    endDate: '',
    selectedUserType: [],
    selectedState: 'Todos',
    selectedCity: 'Todos'
  });

  const [appliedFilters, setAppliedFilters] = useState({
    selectedChapter: '',
    selectedTag: '',
    startDate: '',
    endDate: '',
    selectedUserType: [],
    selectedState: 'Todos',
    selectedCity: 'Todos'
  });

  const [showTable, setShowTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [selectedUsers, setSelectedUsers] = useState(new Set());

  const mapContainerRef = useRef(null);
  const chartsContainerRef = useRef(null);
  //#endregion

  //#region useEffect - Buscar Dados Iniciais
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const auth = localStorage.getItem("auth");
        const headers = { Authorization: `Basic ${auth}` };

        const [chaptersResponse, tagsResponse, statesResponse] = await Promise.all([
          axios.get(API_ENDPOINTS.CHAPTERS_TITLES, { headers }),
          axios.get(API_ENDPOINTS.CHAPTERS_TAGS, { headers }),
          axios.get('https://servicodados.ibge.gov.br/api/v1/localidades/estados')
        ]);

        setChapters(chaptersResponse.data);
        setTags(tagsResponse.data);
        setStatesFromIBGE(statesResponse.data.sort((a, b) => a.sigla.localeCompare(b.sigla)));
      } catch (error) {
        console.error('Erro ao buscar dados iniciais:', error);
      }
    };

    fetchInitialData();
  }, []);
  //#endregion

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isMapFullscreen) {
        setIsMapFullscreen(false);
      }
    };

    if (isMapFullscreen) {
      window.addEventListener('keydown', handleKeyDown);

      const escHintElement = document.querySelector('.geo-map-modal-esc-hint');
      if (escHintElement) {
        setTimeout(() => {
          escHintElement.classList.remove('d-md-block');
        }, 5000);
      }

      setTimeout(() => {
        if (mapContainerRef.current) {
          mapContainerRef.current.invalidateSize();
        }
      }, 100);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMapFullscreen]);

  //#region useEffect - Buscar Dados Filtrados
  useEffect(() => {
    const fetchProcessedData = async () => {
      try {
        setLoading(true);
        const auth = localStorage.getItem("auth");

        const params = new URLSearchParams();

        if (appliedFilters.startDate) {
          params.append('startDate', new Date(appliedFilters.startDate).toISOString().replace('.000Z', '.00'));
        }

        if (appliedFilters.endDate) {
          params.append('endDate', new Date(appliedFilters.endDate + 'T23:59:59').toISOString().replace('.000Z', '.00'));
        }

        if (appliedFilters.selectedChapter) params.append('chapterId', appliedFilters.selectedChapter);
        if (appliedFilters.selectedTag) params.append('tagName', appliedFilters.selectedTag);
        if (appliedFilters.selectedUserType.length > 0) {
          params.append('subtypes', appliedFilters.selectedUserType.join(','));
        }
        if (appliedFilters.selectedState !== 'Todos' && appliedFilters.selectedState !== 'Outros') {
          params.append('stateCode', appliedFilters.selectedState);
        }

        const response = await axios.get(`${API_ENDPOINTS.CHAPTER_ACESS_FILTER}?${params}`, {
          headers: { Authorization: `Basic ${auth}` }
        });

        setAccessData(response.data);
      } catch (error) {
        console.error('Erro ao buscar dados filtrados:', error);
        setAccessData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProcessedData();
  }, [appliedFilters]);
  //#endregion

  //#region useEffect - Redimensionamento do Mapa
  useEffect(() => {
    if (mapContainerRef.current && forceUpdate > 0) {
      setTimeout(() => {
        if (mapContainerRef.current) {
          const mapInstance = mapContainerRef.current;
          if (mapInstance._container) {
            mapInstance.invalidateSize();
          }
        }
      }, 200);
    }
  }, [forceUpdate]);

  const applyFilters = () => {
    setSelectedUsers(new Set());
    setAppliedFilters({ ...tempFilters });
    setShowDrawer(false);
  };

  const clearFilters = () => {
    setSelectedUsers(new Set());
    const defaultFilters = {
      selectedChapter: '',
      selectedTag: '',
      startDate: '',
      endDate: '',
      selectedUserType: [],
      selectedState: 'Todos',
      selectedCity: 'Todos'
    };

    setPeriodPreset('');
    setTempFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const updateTempFilter = (key, value) => {
    setTempFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Filtros primários: aplicam em tempo real (mexe -> resultado já atualiza)
  const setPrimary = (patch) => {
    setSelectedUsers(new Set());
    setTempFilters(prev => ({ ...prev, ...patch }));
    setAppliedFilters(prev => ({ ...prev, ...patch }));
  };

  const applyPeriodPreset = (days) => {
    setPeriodPreset(days);
    if (!days) {
      setPrimary({ startDate: '', endDate: '' });
      return;
    }
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - Number(days));
    const fmt = (d) => d.toISOString().split('T')[0];
    setPrimary({ startDate: fmt(start), endDate: fmt(end) });
  };
  //#endregion

  //#region Processamento de Dados
  const processedData = React.useMemo(() => {
    if (!accessData.length) return [];

    let filtered = accessData.map(access => ({
      id: access.id,
      lat: parseFloat(access.locationLatitude) || 0,
      lng: parseFloat(access.locationLongitude) || 0,
      state: access.stateCode || 'Outros',
      city: access.city && access.city.trim() ? access.city : 'Outros',
      ip: access.ip || 'N/A',
      createdAt: access.createdAt ? new Date(access.createdAt) : new Date(),
      userName: access.userName || 'Usuário não identificado',
      userType: access.userSubtype || 'N/A',
      userRole: access.userRole || 'N/A',
      cropType: access.userCrop || 'N/A',
      email: access.userEmail || 'N/A'
    }))
      .filter(access => access.lat !== 0 && access.lng !== 0)
      .filter(access => {
        const stateMatch = appliedFilters.selectedState === 'Todos' ||
          (appliedFilters.selectedState === 'Outros' ? access.state === 'Outros' : access.state === appliedFilters.selectedState);
        const cityMatch = appliedFilters.selectedCity === 'Todos' ||
          (appliedFilters.selectedCity === 'Outros' ? access.city === 'Outros' : access.city === appliedFilters.selectedCity);
        const userTypeMatch = appliedFilters.selectedUserType.length === 0 ||
          appliedFilters.selectedUserType.includes(access.userType);

        return stateMatch && cityMatch && userTypeMatch;
      });

    if (isRadiusFilterActive && radiusFilterCenter) {
      const centerLatLng = L.latLng(radiusFilterCenter.lat, radiusFilterCenter.lng);
      filtered = filtered.filter(access => {
        const pointLatLng = L.latLng(access.lat, access.lng);
        const distance = centerLatLng.distanceTo(pointLatLng);
        return distance <= radius * 1000;
      });
    }

    return filtered;
  }, [accessData, appliedFilters, isRadiusFilterActive, radiusFilterCenter, radius]);

  const sortedData = React.useMemo(() => {
    setCurrentPage(1);
    let sortableData = [...processedData];

    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'createdAt') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableData;
  }, [processedData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);
  //#endregion

  //#region useEffect - Seleção de Registros
  useEffect(() => {
    if (processedData.length > 0) {
      const allFilteredIds = new Set(processedData.map(item => item.id));
      setSelectedUsers(allFilteredIds);
    } else {
      setSelectedUsers(new Set());
    }
  }, [processedData]);
  //#endregion

  //#region Dados Selecionados e Preparação para Gráficos
  const selectedData = processedData.filter(access => selectedUsers.has(access.id));

  const stateData = React.useMemo(() => {
    const stateCount = {};
    selectedData.forEach(item => {
      stateCount[item.state] = (stateCount[item.state] || 0) + 1;
    });
    return Object.entries(stateCount)
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [selectedData]);

  const userTypeData = React.useMemo(() => {
    const typeCount = {};
    selectedData.forEach(item => {
      typeCount[item.userType] = (typeCount[item.userType] || 0) + 1;
    });
    return Object.entries(typeCount).map(([type, count]) => ({ type, count }));
  }, [selectedData]);

  const dailyAccessData = React.useMemo(() => {
    const dailyCount = {};
    selectedData.forEach(item => {
      const date = item.createdAt.toISOString().split('T')[0];
      dailyCount[date] = (dailyCount[date] || 0) + 1;
    });
    return Object.entries(dailyCount)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [selectedData]);

  const hourlyAccessData = React.useMemo(() => {
    const hourlyCount = {};
    for (let i = 0; i < 24; i++) {
      hourlyCount[i] = 0;
    }

    selectedData.forEach(item => {
      const hour = item.createdAt.getHours();
      hourlyCount[hour] = (hourlyCount[hour] || 0) + 1;
    });

    return Object.entries(hourlyCount)
      .map(([hour, count]) => ({
        hour: `${hour.padStart(2, '0')}:00`,
        count,
        hourNum: parseInt(hour)
      }))
      .sort((a, b) => a.hourNum - b.hourNum);
  }, [selectedData]);

  const cityData = React.useMemo(() => {
    const cityCount = {};
    selectedData.forEach(item => {
      const cityState = `${item.city} - ${item.state}`;
      cityCount[cityState] = (cityCount[cityState] || 0) + 1;
    });
    return Object.entries(cityCount)
      .map(([city, count]) => ({
        city: city.length > 12 ? city.substring(0, 12) + '...' : city,
        fullCity: city,
        count
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [selectedData]);

  const heatmapData = React.useMemo(() => {
    const locationCount = {};
    selectedData.forEach(item => {
      const key = `${item.lat.toFixed(4)},${item.lng.toFixed(4)}`;
      locationCount[key] = (locationCount[key] || 0) + 1;
    });

    return Object.entries(locationCount).map(([key, count]) => {
      const [lat, lng] = key.split(',');
      return [parseFloat(lat), parseFloat(lng), count];
    });
  }, [selectedData]);
  //#endregion

  //#region Opções de Filtro
  const stateOptions = ['Todos', ...statesFromIBGE.map(state => state.sigla), 'Outros'];

  const cityOptions = ["Todos", ...Array.from(new Set(processedData.map(item => item.city)))];
  if (!cityOptions.includes('Outros') && processedData.some(item => item.city === 'Outros')) {
    cityOptions.push('Outros');
  }

  const userTypeOptions = [{ pt: "Todos", en: "All", value: "Todos" }, ...subTypes];

  const hasFilterChanges = JSON.stringify(tempFilters) !== JSON.stringify(appliedFilters);
  //#endregion

  //#region Funções de Exportação e Paginação dos dados da tabela de registros
  const exportToCSV = () => {
    const csvHeader = [
      'ID', 'Usuário', 'Email', 'Tipo', 'Papel', 'Cultura',
      'Cidade', 'Estado', 'IP', 'Latitude', 'Longitude', 'Data de Acesso'
    ];

    const csvData = selectedData.map(access => [
      access.id, access.userName, access.email, access.userType, access.userRole,
      access.cropType, access.city, access.state, access.ip, access.lat, access.lng,
      access.createdAt.toLocaleString('pt-BR')
    ]);

    const csvContent = [csvHeader, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);

    const fileName = `dados_acesso_selecionados_${selectedData.length}_registros_${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute('download', fileName);

    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort className='text-muted' />;
    }
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="text-success ms-1" />
      : <FaSortDown className="text-success ms-1" />;
  };
  //#endregion

  //#region Funções de Seleção de Dados da tabela
  const mapData = React.useMemo(() => {
    return processedData.filter(access => selectedUsers.has(access.id));
  }, [processedData, selectedUsers]);

  const handleSelectAllVisible = () => {
    const visibleIds = new Set(paginatedData.map(item => item.id));
    const newSelected = new Set([...selectedUsers, ...visibleIds]);
    setSelectedUsers(newSelected);
  };

  const handleSelectAllFiltered = () => {
    const allFilteredIds = new Set(processedData.map(item => item.id));
    setSelectedUsers(allFilteredIds);
  };

  const handleDeselectAllVisible = () => {
    const visibleIds = new Set(paginatedData.map(item => item.id));
    const newSelected = new Set([...selectedUsers].filter(id => !visibleIds.has(id)));
    setSelectedUsers(newSelected);
  };

  const handleDeselectAll = () => {
    setSelectedUsers(new Set());
  };

  const handleInvertSelection = () => {
    const allFilteredIds = new Set(processedData.map(item => item.id));
    const newSelected = new Set();

    allFilteredIds.forEach(id => {
      if (!selectedUsers.has(id)) {
        newSelected.add(id);
      }
    });

    setSelectedUsers(newSelected);
  };

  const handleUserSelect = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleUserTypeCheckboxChange = (e) => {
    const { value, checked } = e.target;
    let newSelectedUserType = [...tempFilters.selectedUserType];

    if (value === 'Todos') {
      if (checked) {
        newSelectedUserType = [];
      }
    } else {
      if (checked) {
        if (!newSelectedUserType.includes(value)) {
          newSelectedUserType.push(value);
        }
      } else {
        newSelectedUserType = newSelectedUserType.filter(type => type !== value);
      }
    }

    updateTempFilter('selectedUserType', newSelectedUserType);
  };

  const isAllVisibleSelected = paginatedData.length > 0 &&
    paginatedData.every(item => selectedUsers.has(item.id));

  const isAllFilteredSelected = processedData.length > 0 &&
    processedData.every(item => selectedUsers.has(item.id));

  const handleMapClick = useCallback((latlng) => {
    if (isRadiusFilterActive) {
      setRadiusFilterCenter(latlng);
    }
  }, [isRadiusFilterActive]);
  //#endregion

  //#region Renderização Condicionada a flag loading
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }
  //#endregion

  // KPIs e insights (calculados sobre os dados exibidos)
  const kpiTotal = selectedData.length;
  const kpiUsers = new Set(selectedData.map((d) => d.email)).size;
  const kpiTopState = stateData[0] || null;
  const kpiTopStatePct = kpiTotal ? Math.round(((kpiTopState?.count || 0) / kpiTotal) * 100) : 0;
  const kpiPeakHour = hourlyAccessData.reduce((m, h) => (h.count > (m?.count || 0) ? h : m), null);
  const kpiTopUserType = [...userTypeData].sort((a, b) => b.count - a.count)[0] || null;

  // drill-down por estado, remover um filtro
  const applyStateFilter = (state) => {
    if (!state) return;
    setPrimary({ selectedState: state });
  };
  const clearOneFilter = (patch) => {
    setTempFilters((prev) => ({ ...prev, ...patch }));
    setAppliedFilters((prev) => ({ ...prev, ...patch }));
  };

  const activeChips = [];
  if (appliedFilters.selectedState && appliedFilters.selectedState !== 'Todos')
    activeChips.push({ label: `Estado: ${appliedFilters.selectedState}`, clear: () => clearOneFilter({ selectedState: 'Todos' }) });
  if (appliedFilters.selectedCity && appliedFilters.selectedCity !== 'Todos')
    activeChips.push({ label: `Cidade: ${appliedFilters.selectedCity}`, clear: () => clearOneFilter({ selectedCity: 'Todos' }) });
  if (appliedFilters.selectedChapter)
    activeChips.push({ label: `Capítulo: ${(chapters.find((c) => String(c.id) === String(appliedFilters.selectedChapter)) || {}).title || appliedFilters.selectedChapter}`, clear: () => clearOneFilter({ selectedChapter: '' }) });
  if (appliedFilters.selectedTag)
    activeChips.push({ label: `Tag: ${appliedFilters.selectedTag}`, clear: () => clearOneFilter({ selectedTag: '' }) });
  if (appliedFilters.startDate || appliedFilters.endDate)
    activeChips.push({ label: `Período: ${appliedFilters.startDate || '…'} → ${appliedFilters.endDate || '…'}`, clear: () => { setPeriodPreset(''); clearOneFilter({ startDate: '', endDate: '' }); } });
  (appliedFilters.selectedUserType || []).forEach((ut) =>
    activeChips.push({ label: `Perfil: ${ut}`, clear: () => clearOneFilter({ selectedUserType: appliedFilters.selectedUserType.filter((x) => x !== ut) }) }));

  // Abas dos gráficos (painel lateral)
  const chartTabs = [
    { key: 'estados', label: 'Estados' },
    { key: 'hora', label: 'Por hora' },
    { key: 'data', label: 'Por data' },
    { key: 'cidades', label: 'Cidades' },
    { key: 'perfil', label: 'Perfil' },
  ];

  // Mapa reaproveitado (normal e fullscreen)
  const renderMap = () => (
    <MapContainer
      ref={mapContainerRef}
      center={[-15.7797, -47.9297]}
      zoom={5}
      style={{ height: '100%', width: '100%' }}
      attributionControl={true}
    >
      <TileLayer
        attribution='© <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {isRadiusFilterActive && <MapClickHandler onMapClick={handleMapClick} />}
      {isRadiusFilterActive && radiusFilterCenter && <RadiusCircle center={radiusFilterCenter} radius={radius} />}
      {showHeatmap && <HeatmapLayer points={heatmapData} />}
      {showMarkers && mapData.map((access, idx) => (
        <Marker key={`${access.id}-${idx}`} position={[access.lat, access.lng]}>
          <Popup>
            <div>
              <strong>{access.city} - {access.state}</strong><br />
              <strong>Usuário:</strong> {access.userName}<br />
              <strong>Tipo:</strong> {access.userType}<br />
              <strong>Data:</strong> {access.createdAt.toLocaleString('pt-BR')}
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );

  //#region Renderização Principal
  return (
    <div className="container-fluid p-3 geo-dash">
      {/* Cabeçalho: título + botão de filtros avançados */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 geo-title">Acessos por região</h4>
        <Button variant="outline-secondary" size="sm" onClick={() => setShowDrawer(true)}>
          <FaSlidersH className="me-2" />Filtros avançados
        </Button>
      </div>

      {/* Barra de filtros primários (tempo real) */}
      <div className="geo-filterbar mb-3">
        <div className="gf-item">
          <span className="gf-label">Período</span>
          <BrandSelect
            size="sm"
            value={periodPreset}
            onChange={applyPeriodPreset}
            options={[
              { value: '', label: 'Todo o período' },
              { value: '7', label: 'Últimos 7 dias' },
              { value: '30', label: 'Últimos 30 dias' },
              { value: '90', label: 'Últimos 90 dias' },
              { value: '365', label: 'Último ano' },
            ]}
          />
        </div>
        <div className="gf-item">
          <span className="gf-label">Estado</span>
          <BrandSelect
            size="sm"
            value={appliedFilters.selectedState}
            onChange={(v) => setPrimary({ selectedState: v })}
            options={stateOptions.map((s) => ({ value: s, label: s }))}
          />
        </div>
        <div className="gf-item" style={{ minWidth: 220 }}>
          <span className="gf-label">Capítulo</span>
          <BrandSelect
            size="sm"
            value={appliedFilters.selectedChapter}
            onChange={(v) => setPrimary({ selectedChapter: v })}
            placeholder="Todos"
            options={[
              { value: '', label: 'Todos' },
              ...chapters.map((c) => ({ value: c.id, label: c.title })),
            ]}
          />
        </div>
        <button className="btn btn-sm btn-outline-secondary ms-auto" onClick={clearFilters} title="Limpar todos os filtros">
          Limpar filtros
        </button>
      </div>

      {/* Chips de filtros ativos */}
      {activeChips.length > 0 && (
        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <span className="small text-muted me-1">Filtros ativos:</span>
          {activeChips.map((chip, i) => (
            <span key={i} className="geo-chip">
              {chip.label}
              <button type="button" className="geo-chip-x" onClick={chip.clear} aria-label="Remover filtro">×</button>
            </span>
          ))}
          <button type="button" className="btn btn-sm btn-link text-decoration-none p-0 ms-1" onClick={clearFilters}>
            Limpar tudo
          </button>
        </div>
      )}

      {/* KPI strip */}
      <div className="row g-2 g-md-3 mb-3">
        <div className="col-6 col-md-3">
          <div className="card kpi-card h-100"><div className="card-body">
            <div className="kpi-value">{kpiTotal}</div>
            <div className="kpi-label">Acessos no período</div>
          </div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card kpi-card h-100"><div className="card-body">
            <div className="kpi-value">{kpiUsers}</div>
            <div className="kpi-label">Usuários</div>
          </div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card kpi-card h-100"><div className="card-body">
            <div className="kpi-value">{kpiTopState ? kpiTopState.state : '—'}</div>
            <div className="kpi-label">{kpiTopState ? `Estado líder (${kpiTopStatePct}%)` : 'Estado líder — sem dados'}</div>
          </div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card kpi-card h-100"><div className="card-body">
            <div className="kpi-value">{kpiPeakHour && kpiPeakHour.count > 0 ? kpiPeakHour.hour : '—'}</div>
            <div className="kpi-label">{kpiPeakHour && kpiPeakHour.count > 0 ? 'Horário de pico' : 'Horário de pico — sem dados'}</div>
          </div></div>
        </div>
      </div>

      {/* Insight automático */}
      {kpiTotal > 0 && (
        <div className="geo-insight mb-3">
          <FaLightbulb className="me-1" /> {kpiTopState ? `${kpiTopState.state} concentra ${kpiTopStatePct}% dos acessos` : ''}
          {kpiPeakHour && kpiPeakHour.count > 0 ? `; pico de acesso às ${kpiPeakHour.hour}` : ''}
          {kpiTopUserType && kpiTopUserType.type !== 'N/A' ? `; perfil mais ativo: ${kpiTopUserType.type}` : ''}.
        </div>
      )}

      {/* Mapa central (8) + painel de gráficos (4) */}
      <div ref={chartsContainerRef} className="row g-3 geo-main">
        {!isMapFullscreen && (
          <div className="col-lg-8">
            <div className="card h-100">
              <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <div className="geo-map-toggles">
                  <button
                    className={`btn btn-sm ${showMarkers ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setShowMarkers(!showMarkers)}
                  >
                    {showMarkers ? '◉' : '○'} Marcadores
                  </button>
                  <button
                    className={`btn btn-sm ${showHeatmap ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setShowHeatmap(!showHeatmap)}
                  >
                    {showHeatmap ? '◉' : '○'} Mapa de calor
                  </button>
                </div>
                <button
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center p-1"
                  onClick={() => setIsMapFullscreen(true)}
                  title="Expandir mapa"
                >
                  <FaExpand size={18} />
                </button>
              </div>
              <div className="card-body card-body-geo p-2" style={{ minHeight: 420 }}>
                {kpiTotal === 0 ? (
                  <div className="geo-empty h-100 d-flex flex-column justify-content-center align-items-center">
                    <div className="empty-state-icon"><FaSearch size={26} /></div>
                    <p className="mb-1 fw-semibold">Nenhum acesso com esses filtros</p>
                    <p className="small mb-3">Tente ampliar o período ou remover algum filtro.</p>
                    <div className="d-flex gap-2 flex-wrap justify-content-center">
                      <button className="btn btn-sm btn-brand" onClick={() => applyPeriodPreset('')}>Todo o período</button>
                      {appliedFilters.selectedState !== 'Todos' && (
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => clearOneFilter({ selectedState: 'Todos' })}>Remover estado</button>
                      )}
                      <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>Limpar tudo</button>
                    </div>
                  </div>
                ) : renderMap()}
              </div>
            </div>
          </div>
        )}

        {/* Painel lateral: gráficos em abas */}
        <div className="col-lg-4">
          <div className="card h-100">
            <div className="card-header py-2">
              <div className="geo-tabs">
                {chartTabs.map((t) => (
                  <button
                    key={t.key}
                    className={`geo-tab${activeChart === t.key ? ' active' : ''}`}
                    onClick={() => setActiveChart(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="card-body card-body-geo p-2 d-flex flex-column">
              <div style={{ flex: 1, minHeight: 300 }}>
                {kpiTotal === 0 ? (
                  <div className="text-muted small d-flex h-100 align-items-center justify-content-center text-center">
                    Sem dados para exibir com os filtros atuais.
                  </div>
                ) : activeChart === 'estados' ? (
                  <ResponsiveChartContainer forceUpdate={forceUpdate}>
                    <BarChart data={stateData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,.25)" />
                      <XAxis dataKey="state" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#339288" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => applyStateFilter(d && d.state)} />
                    </BarChart>
                  </ResponsiveChartContainer>
                ) : activeChart === 'hora' ? (
                  <ResponsiveChartContainer forceUpdate={forceUpdate}>
                    <AreaChart data={hourlyAccessData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,.25)" />
                      <XAxis dataKey="hour" interval={3} tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Area type="monotone" dataKey="count" stroke="#339288" fill="#339288" fillOpacity={0.55} />
                    </AreaChart>
                  </ResponsiveChartContainer>
                ) : activeChart === 'data' ? (
                  <ResponsiveChartContainer forceUpdate={forceUpdate}>
                    <LineChart data={dailyAccessData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,.25)" />
                      <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toISOString().split('T')[0]} tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip labelFormatter={(date) => new Date(date).toISOString().split('T')[0]} />
                      <Line type="monotone" dataKey="count" stroke="#339288" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveChartContainer>
                ) : activeChart === 'cidades' ? (
                  <ResponsiveChartContainer forceUpdate={forceUpdate}>
                    <BarChart data={cityData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,.25)" />
                      <XAxis dataKey="city" tick={{ fontSize: 9 }} interval={0} angle={20} textAnchor="start" />
                      <YAxis />
                      <Tooltip labelFormatter={(label) => { const item = cityData.find(d => d.city === label); return item ? item.fullCity : label; }} />
                      <Bar dataKey="count" fill="#3c8c71" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveChartContainer>
                ) : (
                  <ResponsiveChartContainer forceUpdate={forceUpdate}>
                    <PieChart>
                      <Pie data={userTypeData} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius="80%"
                        label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}>
                        {userTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveChartContainer>
                )}
              </div>

              <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                <small className="text-muted">
                  {processedData.length} no filtro · {selectedUsers.size} no mapa
                </small>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={exportToCSV}
                  disabled={selectedUsers.size === 0}
                  title={selectedUsers.size === 0 ? 'Nenhum registro para exportar' : `Exportar ${selectedUsers.size} registro(s)`}
                >
                  ⤓ CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL FULLSCREEN do mapa */}
      {isMapFullscreen && (
        <div className="geo-map-modal">
          <div className="geo-map-modal-content">
            <div className="geo-map-modal-header">
              <h5 className="mb-0">Mapa de Acessos</h5>
              <Button variant="close" onClick={() => setIsMapFullscreen(false)} aria-label="Fechar" />
            </div>
            <div className="geo-map-modal-body">
              {renderMap()}
            </div>
            <div className="geo-map-modal-esc-hint d-none d-md-block">
              Pressione <kbd>ESC</kbd> para sair
            </div>
          </div>
        </div>
      )}

      {/* Tabela de dados (colapsável) */}
      <div className="row mt-3">
        <div className="col-12">
          <div className="card">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Dados de Acesso</h6>
              <div className="d-flex align-items-center gap-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showTableSwitch"
                    checked={showTable}
                    onChange={(e) => setShowTable(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showTableSwitch">
                    Mostrar Tabela
                  </label>
                </div>

                <button
                  className="btn btn-sm btn-success"
                  onClick={exportToCSV}
                  disabled={selectedUsers.size === 0}
                  title={selectedUsers.size === 0 ? 'Selecione pelo menos um registro para exportar' : `Exportar ${selectedUsers.size} registro(s) selecionado(s)`}
                >
                  <i className="fas fa-download me-2"></i>
                  Exportar CSV ({selectedUsers.size})
                </button>
              </div>
            </div>

            {showTable ? (
              processedData.length > 0 ? (
                <div className="card-body card-body-geo">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="text-muted">
                      Mostrando {startIndex + 1} a {Math.min(endIndex, sortedData.length)} de {sortedData.length} registros
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0">Linhas por página:</label>
                      <BrandSelect
                        size="sm"
                        style={{ width: 'auto', minWidth: '78px' }}
                        value={itemsPerPage}
                        onChange={(v) => handleItemsPerPageChange(Number(v))}
                        options={[
                          { value: 25, label: '25' },
                          { value: 50, label: '50' },
                          { value: 100, label: '100' },
                        ]}
                      />
                    </div>
                  </div>

                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    <button className="btn btn-sm btn-outline-success" onClick={handleSelectAllFiltered} disabled={isAllFilteredSelected}>
                      Selecionar Todos Filtrados ({processedData.length})
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={handleSelectAllVisible} disabled={isAllVisibleSelected}>
                      Selecionar Página Atual ({paginatedData.length})
                    </button>
                    <button className="btn btn-sm btn-outline-info" onClick={handleInvertSelection} disabled={processedData.length === 0}
                      title="Inverte a seleção">
                      Inverter Seleção
                    </button>
                    <button className="btn btn-sm btn-outline-warning" onClick={handleDeselectAllVisible}
                      disabled={!paginatedData.some(item => selectedUsers.has(item.id))}>
                      Desmarcar Página Atual
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={handleDeselectAll} disabled={selectedUsers.size === 0}>
                      Desmarcar Todos
                    </button>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-striped table-hover table-sm">
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>
                            <div className="d-flex flex-column align-items-center">
                              <input
                                type="checkbox"
                                className="form-check-input mb-1"
                                checked={isAllVisibleSelected}
                                onChange={isAllVisibleSelected ? handleDeselectAllVisible : handleSelectAllVisible}
                                style={{ transform: 'scale(1.2)' }}
                              />
                              <small className="text-muted" style={{ fontSize: '0.7rem' }}>Página</small>
                            </div>
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>ID {getSortIcon('id')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('userName')}>Usuário {getSortIcon('userName')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>Email {getSortIcon('email')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('userType')}>Tipo {getSortIcon('userType')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('userRole')}>Papel {getSortIcon('userRole')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('cropType')}>Cultura {getSortIcon('cropType')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('city')}>Cidade {getSortIcon('city')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('state')}>Estado {getSortIcon('state')}</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('ip')}>IP {getSortIcon('ip')}</th>
                          <th>Coordenadas</th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>Data de Acesso {getSortIcon('createdAt')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((access, index) => (
                          <tr key={`${access.id}-${index}`}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedUsers.has(access.id)}
                                onChange={() => handleUserSelect(access.id)}
                                style={{ transform: 'scale(1.2)' }}
                              />
                            </td>
                            <td>{access.id}</td>
                            <td>{access.userName}</td>
                            <td>{access.email}</td>
                            <td>{access.userType}</td>
                            <td>{access.userRole}</td>
                            <td>{access.cropType}</td>
                            <td>{access.city}</td>
                            <td>{access.state}</td>
                            <td>{access.ip}</td>
                            <td>{access.lat.toFixed(4)}, {access.lng.toFixed(4)}</td>
                            <td>{access.createdAt.toLocaleString('pt-BR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <Pagination className="justify-content-center mt-3 mb-0">
                      <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                      <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
                        const pageOffset = 1;
                        const showAll = totalPages <= 4;
                        if (showAll || pageNumber === 1 || pageNumber === totalPages ||
                          (pageNumber >= currentPage - pageOffset && pageNumber <= currentPage + pageOffset)) {
                          return (
                            <Pagination.Item key={pageNumber} active={pageNumber === currentPage} onClick={() => setCurrentPage(pageNumber)}>
                              {pageNumber}
                            </Pagination.Item>
                          );
                        }
                        if (pageNumber === currentPage - pageOffset - 1 || pageNumber === currentPage + pageOffset + 1) {
                          return <Pagination.Ellipsis disabled key={`ellipsis-${pageNumber}`} />;
                        }
                        return null;
                      })}
                      <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
                      <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                  )}
                </div>
              ) : (
                <div className="card-body card-body-geo">
                  <div className="text-center py-4">
                    <p className="text-muted">Nenhum dado de acesso encontrado com os filtros aplicados.</p>
                  </div>
                </div>
              )
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>

      {/* DRAWER de filtros avançados (agrupados) */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filtros avançados</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="geo-drawer">
          {/* TEMPO */}
          <div className="geo-group">
            <div className="geo-group-title">⏱ Tempo</div>
            <div className="row g-2">
              <div className="col-6">
                <label className="form-label small">Data inicial</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={tempFilters.startDate}
                  onChange={(e) => { setPeriodPreset(''); updateTempFilter('startDate', e.target.value); }}
                />
              </div>
              <div className="col-6">
                <label className="form-label small">Data final</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={tempFilters.endDate}
                  onChange={(e) => { setPeriodPreset(''); updateTempFilter('endDate', e.target.value); }}
                />
              </div>
            </div>
          </div>

          {/* LOCALIZAÇÃO */}
          <div className="geo-group">
            <div className="geo-group-title">Localização</div>
            <div className="row g-2">
              <div className="col-6">
                <label className="form-label small">Estado</label>
                <BrandSelect
                  size="sm"
                  value={tempFilters.selectedState}
                  onChange={(v) => updateTempFilter('selectedState', v)}
                  options={stateOptions.map((s) => ({ value: s, label: s }))}
                />
              </div>
              <div className="col-6">
                <label className="form-label small">Cidade</label>
                <BrandSelect
                  size="sm"
                  value={tempFilters.selectedCity}
                  onChange={(v) => updateTempFilter('selectedCity', v)}
                  options={cityOptions.map((c) => ({ value: c, label: c }))}
                />
              </div>
            </div>
            <div className="mt-2">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="radiusFilterSwitch"
                  checked={isRadiusFilterActive}
                  onChange={(e) => {
                    setIsRadiusFilterActive(e.target.checked);
                    if (!e.target.checked) setRadiusFilterCenter(null);
                  }}
                />
                <label className="form-check-label small" htmlFor="radiusFilterSwitch">
                  Filtrar por raio {isRadiusFilterActive ? `(${radius} km)` : ''}
                </label>
              </div>
              {isRadiusFilterActive && (
                <>
                  <input
                    type="number"
                    className="form-control form-control-sm mt-2"
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                    min="1"
                    max="1000"
                  />
                  <p className="form-text text-muted mb-0 mt-1 small">
                    {radiusFilterCenter
                      ? `Centro: ${radiusFilterCenter.lat.toFixed(4)}, ${radiusFilterCenter.lng.toFixed(4)} · clique no mapa para alterar.`
                      : 'Clique no mapa para definir o centro.'}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* CONTEÚDO */}
          <div className="geo-group">
            <div className="geo-group-title">Conteúdo</div>
            <div className="row g-2">
              <div className="col-12">
                <label className="form-label small">Capítulo</label>
                <BrandSelect
                  size="sm"
                  value={tempFilters.selectedChapter}
                  onChange={(v) => updateTempFilter('selectedChapter', v)}
                  placeholder="Todos"
                  options={[
                    { value: '', label: 'Todos' },
                    ...chapters.map((c) => ({ value: c.id, label: c.title })),
                  ]}
                />
              </div>
              <div className="col-12">
                <label className="form-label small">Tag</label>
                <BrandSelect
                  size="sm"
                  value={tempFilters.selectedTag}
                  onChange={(v) => updateTempFilter('selectedTag', v)}
                  placeholder="Todas"
                  options={[
                    { value: '', label: 'Todas' },
                    ...tags.map((t) => ({ value: t, label: t })),
                  ]}
                />
              </div>
            </div>
          </div>

          {/* PERFIL DO USUÁRIO */}
          <div className="geo-group">
            <div className="geo-group-title">Perfil do usuário</div>
            <div className="geo-type-box" style={{ maxHeight: 180, overflowY: 'auto' }}>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="userTypeAll"
                  checked={tempFilters.selectedUserType.length === 0}
                  onChange={handleUserTypeCheckboxChange}
                  value="Todos"
                />
                <label className="form-check-label small" htmlFor="userTypeAll">Todos</label>
              </div>
              {userTypeOptions.filter(type => type.value !== 'Todos').map(type => (
                <div className="form-check" key={type.id}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`userType-${type.id}`}
                    checked={tempFilters.selectedUserType.includes(type.value)}
                    onChange={handleUserTypeCheckboxChange}
                    value={type.value}
                  />
                  <label className="form-check-label small" htmlFor={`userType-${type.id}`}>{type.pt}</label>
                </div>
              ))}
            </div>
          </div>
        </Offcanvas.Body>
        <div className="geo-drawer-footer">
          <button className="btn btn-outline-secondary" onClick={clearFilters}>Limpar</button>
          <button
            className={`btn ${hasFilterChanges ? 'btn-brand' : 'btn-outline-secondary'}`}
            onClick={applyFilters}
            disabled={!hasFilterChanges}
          >
            {hasFilterChanges ? 'Aplicar filtros' : 'Aplicados'}
          </button>
        </div>
      </Offcanvas>
    </div>
  );
};

export default Geo;
//#endregion
